import { MenuItem, Typography, useTheme, Collapse, Box } from "@mui/material";
import { FC, useState, useMemo, useCallback, useContext } from "react";
import { Link as RouteLink, useLocation, useParams } from "react-router-dom";

import { NavMenuItem } from "../../../api/dtos/dtos";
import { IHasChildren } from "../types/IHasChildren";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTriangle } from "@fortawesome/pro-regular-svg-icons";
import CommonContext from "../../../contexts/data/CommonContext";

interface INavButtonProps extends IHasChildren {
    isDefault?: string;
    to: string;
    menuItems?: NavMenuItem[];
}

const NavButton: FC<INavButtonProps> = ({ isDefault, to, menuItems, children }) => {
    const theme = useTheme();
    const { page } = useParams();
    const location = useLocation();

    const [open, setOpen] = useState(false);

    const { setMobileNavigationDrawerOpen } = useContext(CommonContext);

    const isExternal = useMemo(() => {
        return to?.startsWith("http://") || to?.startsWith("https://");
    }, [to]);

    const isSelected = useMemo(() => {
        return (
            (page &&
                (to?.endsWith(page) ||
                    !!menuItems?.find((x) => x.link?.endsWith(page)))) ||
            location.pathname.endsWith(to) ||
            (isDefault && location.pathname?.endsWith(isDefault))
        );
    }, [page, location.pathname, to, menuItems, isDefault]);

    const handleToggleMenu = useCallback(() => setOpen((prev) => !prev), []);

    return (
        <Box>
            <MenuItem
                component={isExternal ? "a" : RouteLink}
                href={isExternal ? to : undefined}
                to={!isExternal ? to : undefined}
                target={isExternal ? "_blank" : undefined}
                rel={isExternal ? "noopener noreferrer" : undefined}
                onClick={(e) => {
                    const hasMenuItems = !!menuItems?.length;

                    if (!hasMenuItems) {
                        setMobileNavigationDrawerOpen(false);
                        return;
                    }

                    if (!isExternal) {
                        e.preventDefault();
                        handleToggleMenu();
                    }
                }}
                sx={{
                    borderBottom: `4px solid ${isSelected ? theme.palette.secondary.main : "transparent"}`,
                    py: 1.5,
                    px: 2,
                    color: isSelected ? `${theme.palette.secondary.main} !important` : "#333 !important",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    "& svg": {
                        color: isSelected ? `${theme.palette.secondary.main} !important` : "#333 !important",
                        fill: isSelected ? `${theme.palette.secondary.main} !important` : "#333 !important",
                    },
                }}
            >
                <Typography variant="body1">{children}</Typography>
                {menuItems && menuItems.length > 0 && (
                    <Box
                        component="span"
                        sx={{ transform: open ? "rotate(180deg)" : "rotate(90deg)", transition: "0.2s" }}
                    >
                        <FontAwesomeIcon icon={faTriangle} />
                    </Box>
                )}
            </MenuItem>

            {menuItems && menuItems.length > 0 && (
                <Collapse in={open} timeout="auto" unmountOnExit>
                    {menuItems
                        .filter((x) => x.name !== "Admin")
                        .sort((a, b) => a.rank - b.rank)
                        .map((x) => (
                            <NavButton
                                key={`nav-${x.rank}-${x.id}`}
                                to={x.link}
                                menuItems={x.items}
                            >
                                <Typography variant="body2" sx={{ pl: 3 }}>
                                    {x.name}
                                </Typography>
                            </NavButton>
                        ))}
                </Collapse>
            )}
        </Box>
    );
};

export default NavButton;
